import { useEffect } from "react";
import { useLocation } from "wouter";
import { serviceData } from "@/pages/services/serviceData";
import { faqItems } from "@/lib/faq";

const siteUrl = "https://tts-britz.de";
const siteName = "Tür- und Torservice Britz";
const defaultDescription =
  "Tür- und Torservice Britz aus Öhringen: Meisterbetrieb für Garagentore, Industrietore, Brandschutztüren, UVV-Prüfungen, Einbruchschutz und Metallbauarbeiten.";
const mainNavigation = [
  { name: "Leistungen", url: `${siteUrl}/leistungen` },
  { name: "Garagentore", url: `${siteUrl}/garagentore` },
  { name: "Industrietore", url: `${siteUrl}/industrietore` },
  { name: "Brandschutztüren und Tore", url: `${siteUrl}/brandschutztueren-tore` },
  { name: "UVV-Prüfungen", url: `${siteUrl}/uvv-pruefung` },
  { name: "Metallbauarbeiten", url: `${siteUrl}/metallbauarbeiten` },
  { name: "Einbruchschutz", url: `${siteUrl}/einbruchschutz` },
  { name: "Verladebrücken", url: `${siteUrl}/verladebruecken` },
  { name: "Über uns", url: `${siteUrl}/ueber-uns` },
  { name: "Zertifikate", url: `${siteUrl}/zertifikate` },
  { name: "Kontakt", url: `${siteUrl}/kontakt` },
];

const staticPages: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Tür- und Torservice Britz | Öhringen",
    description: defaultDescription,
  },
  "/ueber-uns": {
    title: "Über uns | Tür- und Torservice Britz aus Öhringen",
    description:
      "Tür- und Torservice Britz aus Öhringen-Eckartsweiler: regionaler Meisterbetrieb für Türen, Tore, Metallbau und Sicherheit in Hohenlohe.",
  },
  "/kontakt": {
    title: "Kontakt | Tür- und Torservice Britz in Öhringen",
    description:
      "Kontakt zu Tür- und Torservice Britz in Öhringen-Eckartsweiler: Anfrage zu Türen, Toren, UVV-Prüfungen, Metallbau und Sicherheit stellen.",
  },
  "/leistungen": {
    title: "Leistungen | Tür- und Torservice Britz in Öhringen",
    description:
      "Unsere Leistungen: Garagentore, Industrietore, Brandschutztüren, UVV-Prüfungen, Metallbauarbeiten, Einbruchschutz und Verladebrücken in der Region Hohenlohe.",
  },
  "/zertifikate": {
    title: "Zertifikate & Nachweise | Tür- und Torservice Britz",
    description:
      "Zertifikate, Meisterbriefe und Nachweise von Tür- und Torservice Britz: Qualifikationen, ISO 9001:2015 und fachliche Nachweise.",
  },
  "/impressum": {
    title: "Impressum | Tür- und Torservice Britz",
    description: "Impressum und Anbieterkennzeichnung von Tür- und Torservice Britz aus Öhringen-Eckartsweiler.",
  },
  "/datenschutz": {
    title: "Datenschutz | Tür- und Torservice Britz",
    description: "Datenschutzerklärung von Tür- und Torservice Britz.",
  },
};

const servicePages: Record<string, { title: string; description: string }> = {
  "/garagentore": {
    title: "Garagentore in Öhringen | Reparatur, Wartung & Service",
    description:
      "Tür- und Torservice Britz ist Ihr Ansprechpartner für Garagentore in Öhringen und Umgebung: Reparatur, Wartung, Service und Lösungen rund ums Tor.",
  },
  "/industrietore": {
    title: "Industrietore in Öhringen | Wartung, Reparatur & Prüfung",
    description:
      "Service für Industrietore in Öhringen, Hohenlohe und Umgebung: Wartung, Reparatur, Instandsetzung und Prüfung durch den Meisterbetrieb Britz.",
  },
  "/brandschutztueren-tore": {
    title: "Brandschutztüren & Brandschutztore in Öhringen",
    description:
      "Montage, Wartung und Instandsetzung von Brandschutztüren und Brandschutztoren in Öhringen und Umgebung durch Tür- und Torservice Britz.",
  },
  "/uvv-pruefung": {
    title: "UVV-Prüfungen für Tore & Türen in Öhringen",
    description:
      "Tür- und Torservice Britz führt UVV-Prüfungen an Türen, Toren und Toranlagen in Öhringen, Hohenlohe und Umgebung fachgerecht durch.",
  },
  "/metallbauarbeiten": {
    title: "Metallbauarbeiten in Öhringen | Tür- und Torservice Britz",
    description:
      "Metallbauarbeiten vom Meisterbetrieb in Öhringen: Tür- und Torservice Britz unterstützt Privatkunden, Gewerbe, Industrie und Hausverwaltungen.",
  },
  "/einbruchschutz": {
    title: "Einbruchschutz in Öhringen | Türen, Tore & Fenstersicherung",
    description:
      "Einbruchschutz für Türen, Tore und Fenster in Öhringen und Umgebung: Beratung, Montage und Sicherheitslösungen vom Meisterbetrieb Britz.",
  },
  "/verladebruecken": {
    title: "Verladebrücken in Öhringen | Wartung, Service & Montage",
    description:
      "Tür- und Torservice Britz unterstützt bei Verladebrücken in Öhringen und Umgebung: Montage, Wartung, Prüfung und Instandsetzung für Gewerbe und Industrie.",
  },
};

const fallbackServiceTitleSuffix = "Öhringen | Tür- und Torservice Britz";

const normalizePath = (path: string) => {
  const cleanPath = path.split("?")[0].split("#")[0];
  if (cleanPath === "/index.html") return "/";
  return cleanPath === "" ? "/" : cleanPath;
};

const pathsWithFaq = new Set(["/", "/ueber-uns", "/kontakt"]);

const trimDescription = (text: string) => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 158) return cleaned;
  return `${cleaned.slice(0, 155).trim()}...`;
};

const setMetaTag = (selector: string, attr: "content" | "href", value: string, create: () => HTMLMetaElement | HTMLLinkElement) => {
  let element = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);

  if (!element) {
    element = create();
    document.head.appendChild(element);
  }

  element.setAttribute(attr, value);
};

const upsertJsonLd = (id: string, data: Record<string, unknown>) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
};

const removeJsonLd = (id: string) => {
  document.getElementById(id)?.remove();
};

export default function SEO() {
  const [location] = useLocation();

  useEffect(() => {
    const path = normalizePath(location);
    const service = serviceData.find((item) => item.route === path);
    const page = service
      ? servicePages[path] ?? {
          title: `${service.title} ${fallbackServiceTitleSuffix}`,
          description: trimDescription(service.subtitle || service.description[0] || defaultDescription),
        }
      : staticPages[path] ?? {
          title: `${siteName} | Türen, Tore & Metallbau in Öhringen`,
          description: defaultDescription,
        };

    const canonicalUrl = `${siteUrl}${path === "/" ? "/" : path}`;
    const imageUrl = `${siteUrl}/opengraph.jpg`;

    document.title = page.title;

    setMetaTag('meta[name="robots"]', "content", "index,follow", () => {
      const meta = document.createElement("meta");
      meta.name = "robots";
      return meta;
    });

    setMetaTag('meta[name="description"]', "content", page.description, () => {
      const meta = document.createElement("meta");
      meta.name = "description";
      return meta;
    });

    setMetaTag('link[rel="canonical"]', "href", canonicalUrl, () => {
      const link = document.createElement("link");
      link.rel = "canonical";
      return link;
    });

    [
      ["og:locale", "de_DE"],
      ["og:type", service ? "article" : "website"],
      ["og:site_name", siteName],
      ["og:title", page.title],
      ["og:description", page.description],
      ["og:url", canonicalUrl],
      ["og:image", imageUrl],
      ["twitter:card", "summary_large_image"],
      ["twitter:title", page.title],
      ["twitter:description", page.description],
      ["twitter:image", imageUrl],
    ].forEach(([property, content]) => {
      const selector = property.startsWith("twitter:")
        ? `meta[name="${property}"]`
        : `meta[property="${property}"]`;

      setMetaTag(selector, "content", content, () => {
        const meta = document.createElement("meta");
        if (property.startsWith("twitter:")) {
          meta.name = property;
        } else {
          meta.setAttribute("property", property);
        }
        return meta;
      });
    });

    upsertJsonLd("local-business-schema", {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${siteUrl}/#unternehmen`,
      name: siteName,
      url: siteUrl,
      image: imageUrl,
      logo: `${siteUrl}/favicon.png`,
      telephone: "+49794133514",
      email: "info@tts-britz.de",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Eichklingenweg 7",
        postalCode: "74613",
        addressLocality: "Öhringen-Eckartsweiler",
        addressCountry: "DE",
      },
      areaServed: ["Öhringen", "Hohenlohe", "Neuenstein", "Bretzfeld", "Pfedelbach", "Waldenburg"],
      priceRange: "$$",
      makesOffer: serviceData.map((item) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: item.title,
          url: `${siteUrl}${item.route}`,
          description: trimDescription(item.subtitle || item.description[0]),
        },
      })),
    });

    upsertJsonLd("website-schema", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: siteName,
      alternateName: "TTS Britz",
      url: `${siteUrl}/`,
      inLanguage: "de-DE",
      publisher: {
        "@id": `${siteUrl}/#unternehmen`,
      },
    });

    upsertJsonLd("site-navigation-schema", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${siteUrl}/#seitenstruktur`,
      name: "Wichtige Seiten von Tür- und Torservice Britz",
      itemListElement: mainNavigation.map((item, index) => ({
        "@type": "SiteNavigationElement",
        position: index + 1,
        name: item.name,
        url: item.url,
      })),
    });

    const breadcrumbItems = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Startseite",
        item: `${siteUrl}/`,
      },
    ];

    if (service) {
      breadcrumbItems.push(
        {
          "@type": "ListItem",
          position: 2,
          name: "Leistungen",
          item: `${siteUrl}/leistungen`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: service.title,
          item: canonicalUrl,
        },
      );

      upsertJsonLd("service-schema", {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        name: service.title,
        description: page.description,
        serviceType: service.title,
        url: canonicalUrl,
        provider: {
          "@id": `${siteUrl}/#unternehmen`,
        },
        areaServed: ["Öhringen", "Hohenlohe", "Neuenstein", "Bretzfeld", "Pfedelbach", "Waldenburg"],
      });
    } else if (path !== "/") {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        name: page.title.split("|")[0].trim(),
        item: canonicalUrl,
      });

      removeJsonLd("service-schema");
    } else {
      removeJsonLd("service-schema");
    }

    upsertJsonLd("breadcrumb-schema", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems,
    });

    if (pathsWithFaq.has(path)) {
      upsertJsonLd("faq-schema", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      });
    } else {
      removeJsonLd("faq-schema");
    }
  }, [location]);

  return null;
}
